// src/shared/accessWipe.ts
//
// What a device forgets when the server says this person no longer has
// access (their membership is gone, or their login is blocked).
//
// Wipes the app's own copies of company data on THIS device: offline pass
// and markers, company settings (incl. the logo), price list, proposal
// sections, scope templates, per-estimate notes/timeline/toggles, the recent
// files list, queued emails and the local draft — every localStorage key the
// app writes starts with "du_", plus two legacy settings keys, plus the desktop
// app's electron-store copies (durableRemove clears both).
//
// It does NOT and cannot touch files the person saved (.est/.DUest on disk,
// exported PDFs, anything in their Dropbox). Nothing here tries to.

import { durableRemove } from "../durableStore";

/** Keys the desktop app may also hold in electron-store (not just localStorage). */
const DURABLE_KEYS = [
  "du_user_settings",
  "du_offline_org_id",
  "du_last_online",
  "du_offline_role",
  "du_offline_user_id",
  "du_offline_email",
  "du_auth_session_hint",
  "du_signature_image",
  "du_zoho_lead_list",
];

const LEGACY_KEYS = ["userSettings", "duUserSettings"];

/** Keys that are the viewer's own preferences, not company data. */
const KEEP = new Set(["theme"]);

export function wipeLocalCompanyData(orgIds: Array<string | null | undefined> = []): void {
  for (const k of DURABLE_KEYS) {
    try {
      durableRemove(k);
    } catch {
      /* best effort */
    }
  }
  for (const orgId of orgIds) {
    if (!orgId) continue;
    for (const k of [
      `du_cache:org_proposal_sections::${orgId}`,
      `du_cache:org_proposal_sections_ts::${orgId}`,
    ]) {
      try {
        durableRemove(k);
      } catch {
        /* best effort */
      }
    }
  }
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const ls = window.localStorage;
    const doomed: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (!k || KEEP.has(k)) continue;
      if (k.startsWith("du_") || LEGACY_KEYS.includes(k)) doomed.push(k);
    }
    doomed.forEach((k) => ls.removeItem(k));
  } catch {
    /* storage blocked — nothing more we can do */
  }
}

/** A sign-in refused because the login is blocked (admin "Remove access"). */
export function isBlockedLoginError(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return (
    !!e &&
    (e.code === "user_banned" || /\bbanned\b/i.test(String(e.message || "")))
  );
}

/** "Missing column" errors mean that lookup doesn't apply to this schema — an
 *  answer, not a failure (App tries org_id, then account_id). */
function isMissingColumn(err: unknown, column: string): boolean {
  const m = String((err as { message?: string } | null)?.message || "").toLowerCase();
  return m.includes("column") && m.includes(column) && (m.includes("does not exist") || m.includes("not found"));
}

/**
 * Did the membership lookup actually get an ANSWER from the server? If yes
 * and no row came back, "no company" is final (access removed). If either
 * lookup errored or timed out, it is NOT an answer — the caller keeps working
 * from the cached company (e.g. offline in the field).
 */
export function membershipLookupAnswered(
  q1: { error?: unknown } | null | undefined,
  q2: { error?: unknown } | null | undefined
): boolean {
  if (!q1 || !q2) return false;
  const q1ok = !q1.error || isMissingColumn(q1.error, "org_id");
  const q2ok = !q2.error || isMissingColumn(q2.error, "account_id");
  return q1ok && q2ok;
}
